import { useState } from 'react';
import useSWR, { mutate } from 'swr';

import axiosServices, { fetcher } from 'utils/axios';

import type { RestaurantPublicData, RestaurantResponse, RestaurantType } from 'types/restaurant';

// ==============================|| RESTAURANT Public Info ||============================== //

export function useGetRestaurant(restaurantId: string | number | null) {
  const shouldFetch = restaurantId !== null && restaurantId !== undefined && restaurantId;

  const { data, error, isLoading, isValidating, mutate } = useSWR<RestaurantPublicData>(
    shouldFetch ? `/public/restaurants/${restaurantId}` : null,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  );

  return {
    restaurant: data,
    restaurantLoading: isLoading,
    restaurantError: error,
    restaurantValidating: isValidating,
    restaurantEmpty: !isLoading && !data,
    refetch: mutate
  };
}

// ==============================|| RESTAURANT INFO API HOOKS ||============================== //

interface GetRestaurantInfoParams {
  restaurantId: string | number | null | undefined;
}

export function useGetRestaurantInfo(params: GetRestaurantInfoParams) {
  const { restaurantId } = params;

  const shouldFetch = restaurantId !== null && restaurantId !== undefined && restaurantId !== '';

  const { data, error, isLoading, isValidating, mutate } = useSWR<RestaurantResponse>(
    shouldFetch ? `/restaurants/${restaurantId}` : null,
    shouldFetch ? fetcher : null,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  );

  return {
    restaurant: data?.data as RestaurantType | undefined,
    restaurantLoading: isLoading,
    restaurantError: error,
    restaurantValidating: isValidating,
    refetch: mutate
  };
}

// ==============================|| RESTAURANT UPDATE API HOOKS ||============================== //

export interface UpdateRestaurantParams {
  restaurantId: number;
  name?: string;
  slug?: string;
  phone?: string;
  email?: string;
  auto_reschedule_shifts?: boolean;
  maintains_shifts?: boolean;
  city_id?: number | null;
  address?: string | null;
  covermanager_api_key?: string | null;
  covermanager_restaurant_id?: string | null;
  covermanager_webhook_secret?: string | null;
  opening_time?: string | null;
  closing_time?: string | null;
}

export interface UpdateRestaurantResponse {
  success: boolean;
  message: string;
  data?: RestaurantType;
  fieldErrors?: RestaurantFormFieldErrors;
}

export function useUpdateRestaurant() {
  const [isLoading, setIsLoading] = useState(false);

  const updateRestaurant = async (params: UpdateRestaurantParams): Promise<UpdateRestaurantResponse> => {
    setIsLoading(true);
    try {
      const { restaurantId, ...updateData } = params;
      const sanitizedUpdateData = { ...updateData };

      if (typeof sanitizedUpdateData.phone === 'string') {
        const normalizedPhone = sanitizedUpdateData.phone.trim();

        if (normalizedPhone === '') {
          delete sanitizedUpdateData.phone;
        } else {
          sanitizedUpdateData.phone = normalizedPhone;
        }
      }

      const response = await axiosServices.patch(`/restaurants/${restaurantId}`, sanitizedUpdateData);

      mutate(`/restaurants/${restaurantId}`, undefined, { revalidate: true });

      return {
        success: true,
        message: 'Restaurant updated successfully',
        data: response.data?.data
      };
    } catch (error: any) {
      console.error('Error updating restaurant:', error);
      const fieldErrors = extractRestaurantFieldErrors(error);

      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to update restaurant',
        fieldErrors
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { updateRestaurant, isLoading };
}

// ==============================|| RESTAURANT MODE SWITCH API HOOK ||============================== //

export interface SwitchRestaurantModeParams {
  restaurantId: number;
  mode: RestaurantType['mode'];
  covermanager_api_key?: string;
}

export interface SwitchRestaurantModeResponse {
  success: boolean;
  message: string;
  data?: RestaurantType;
  fieldErrors?: RestaurantFormFieldErrors;
}

export function useSwitchRestaurantMode() {
  const [isLoading, setIsLoading] = useState(false);

  const switchRestaurantMode = async (params: SwitchRestaurantModeParams): Promise<SwitchRestaurantModeResponse> => {
    setIsLoading(true);
    try {
      const { restaurantId, ...payload } = params;
      const response = await axiosServices.patch(`/restaurants/${restaurantId}/mode`, payload);

      mutate(`/restaurants/${restaurantId}`, response.data, { revalidate: false });

      return {
        success: true,
        message: 'Restaurant mode updated successfully',
        data: response.data?.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to update restaurant mode',
        fieldErrors: extractRestaurantFieldErrors(error)
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { switchRestaurantMode, isLoading };
}

// ==============================|| RESTAURANT MANAGER UPDATE API HOOKS ||============================== //

export interface UpdateRestaurantManagerParams {
  restaurantId: number;
  managerName: string;
  managerEmail: string;
}

export interface UpdateRestaurantManagerResponse {
  success: boolean;
  message: string;
  data?: RestaurantType;
  fieldErrors?: RestaurantFormFieldErrors;
}

export function useUpdateRestaurantManager() {
  const [isLoading, setIsLoading] = useState(false);

  const updateRestaurantManager = async (params: UpdateRestaurantManagerParams): Promise<UpdateRestaurantManagerResponse> => {
    setIsLoading(true);
    try {
      const { restaurantId, ...updateData } = params;
      const response = await axiosServices.patch(`/restaurants/${restaurantId}/manager`, updateData);

      mutate(`/restaurants/${restaurantId}`, undefined, { revalidate: true });
      mutate((key) => typeof key === 'string' && key.startsWith('/restaurants'), undefined, { revalidate: true });

      return {
        success: true,
        message: 'Restaurant admin updated successfully',
        data: response.data?.data
      };
    } catch (error: any) {
      console.error('Error updating restaurant admin:', error);
      const fieldErrors = extractRestaurantFieldErrors(error);

      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to update restaurant admin',
        fieldErrors
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { updateRestaurantManager, isLoading };
}

// ==============================|| RESTAURANT LOGO UPLOAD HOOK ||============================== //

export interface UploadRestaurantLogoParams {
  restaurantId: number;
  file: File;
}

export interface UploadRestaurantLogoResponse {
  success: boolean;
  message: string;
  data?: RestaurantType;
}

export function useUploadRestaurantLogo() {
  const [isLoading, setIsLoading] = useState(false);

  const uploadRestaurantLogo = async (params: UploadRestaurantLogoParams): Promise<UploadRestaurantLogoResponse> => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('logo', params.file);

      const response = await axiosServices.post(`/restaurants/${params.restaurantId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      mutate(`/restaurants/${params.restaurantId}`, undefined, { revalidate: true });

      return { success: true, message: 'Logo uploaded successfully', data: response.data?.data };
    } catch (error: any) {
      console.error('Error uploading restaurant logo:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to upload logo'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { uploadRestaurantLogo, isLoading };
}

// ==============================|| RESTAURANT LOGO DELETE HOOK ||============================== //

export interface DeleteRestaurantLogoParams {
  restaurantId: number;
}

export interface DeleteRestaurantLogoResponse {
  success: boolean;
  message: string;
  data?: RestaurantType;
}

export function useDeleteRestaurantLogo() {
  const [isLoading, setIsLoading] = useState(false);

  const deleteRestaurantLogo = async (params: DeleteRestaurantLogoParams): Promise<DeleteRestaurantLogoResponse> => {
    setIsLoading(true);
    try {
      const response = await axiosServices.delete(`/restaurants/${params.restaurantId}/logo`);

      mutate(`/restaurants/${params.restaurantId}`, undefined, { revalidate: true });

      return { success: true, message: 'Logo deleted successfully', data: response.data?.data };
    } catch (error: any) {
      console.error('Error deleting restaurant logo:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to delete logo'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { deleteRestaurantLogo, isLoading };
}

// ==============================|| ACCOUNT CHANGE PASSWORD ||============================== //

export interface ChangePasswordParams {
  current_password: string;
  password: string;
  password_confirmation: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

export function useChangePassword() {
  const [isLoading, setIsLoading] = useState(false);

  const changePassword = async (params: ChangePasswordParams): Promise<ChangePasswordResponse> => {
    setIsLoading(true);
    try {
      await axiosServices.patch('/account/password', params);

      return { success: true, message: 'Password changed successfully' };
    } catch (error: any) {
      console.error('Error changing password:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to change password'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { changePassword, isLoading };
}

// ==============================|| RESTAURANT DELETE ||============================== //

export interface DeleteRestaurantParams {
  restaurantId: number;
}

export interface DeleteRestaurantResponse {
  success: boolean;
  message: string;
}

export function useDeleteRestaurant() {
  const [isLoading, setIsLoading] = useState(false);

  const deleteRestaurant = async (params: DeleteRestaurantParams): Promise<DeleteRestaurantResponse> => {
    setIsLoading(true);
    try {
      await axiosServices.delete(`/restaurants/${params.restaurantId}`);

      return { success: true, message: 'Restaurant deleted successfully' };
    } catch (error: any) {
      console.error('Error deleting restaurant:', error);
      return { success: false, message: error?.response?.data?.message || error?.message || 'Failed to delete restaurant' };
    } finally {
      setIsLoading(false);
    }
  };

  return { deleteRestaurant, isLoading };
}

// ==============================|| RESTAURANTS LIST ||============================== //

export interface RestaurantsListParams {
  city_ids?: number[];
  created_from?: number;
  created_to?: number;
  search?: string;
}

export interface RestaurantsListResponse {
  success: boolean;
  message: string;
  data: RestaurantType[];
}

export function useRestaurantsList() {
  const [isLoading, setIsLoading] = useState(false);

  const restaurantsList = async (params: RestaurantsListParams = {}): Promise<RestaurantsListResponse> => {
    setIsLoading(true);
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (params.city_ids && params.city_ids.length > 0) {
        params.city_ids.forEach((cityId) => queryParams.append('city_ids[]', cityId.toString()));
      }
      if (params.created_from) {
        queryParams.append('created_from', params.created_from.toString());
      }
      if (params.created_to) {
        queryParams.append('created_to', params.created_to.toString());
      }
      if (params.search) {
        queryParams.append('search', params.search);
      }

      const queryString = queryParams.toString();
      const url = queryString ? `/restaurants?${queryString}` : '/restaurants';

      const response = await axiosServices.get(url);

      return { success: true, message: 'Restaurants list fetched successfully', data: response.data?.data };
    } catch (error: any) {
      console.error('Error fetching restaurants list:', error);
      return { data: [], success: false, message: error?.response?.data?.message || error?.message || 'Failed to fetch restaurants list' };
    } finally {
      setIsLoading(false);
    }
  };

  return { restaurantsList, isLoading };
}

// ==============================|| RESTAURANT ADD ||============================== //

export interface AddRestaurantParams {
  name: string;
  slug?: string;
  // Manager data is used only for creation flow
  managerName: string;
  managerEmail: string;
  phone?: string;
  email?: string;
  address: string;
  city_id: number;
  auto_reschedule_shifts?: boolean;
  maintains_shifts: boolean;
  covermanager_api_key?: string;
  covermanager_restaurant_id?: string;
  covermanager_webhook_secret?: string;
  opening_time?: string | null;
  closing_time?: string | null;
}

export type RestaurantFormField =
  | 'name'
  | 'slug'
  | 'phone'
  | 'email'
  | 'managerName'
  | 'managerEmail'
  | 'address'
  | 'city_id'
  | 'covermanager_api_key'
  | 'maintains_shifts'
  | 'mode'
  | 'opening_time'
  | 'closing_time';

export type RestaurantFormFieldErrors = Partial<Record<RestaurantFormField, string>>;

export interface AddRestaurantResponse {
  success: boolean;
  message: string;
  data?: RestaurantType;
  fieldErrors?: RestaurantFormFieldErrors;
}

function extractRestaurantFieldErrors(error: unknown): RestaurantFormFieldErrors | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const rawErrors =
    (error as { errors?: unknown }).errors ?? (error as { response?: { data?: { errors?: unknown } } }).response?.data?.errors;

  if (!rawErrors || typeof rawErrors !== 'object') {
    return undefined;
  }

  const fieldErrors = Object.entries(rawErrors).reduce<RestaurantFormFieldErrors>((accumulator, [field, messages]) => {
    const firstMessage = Array.isArray(messages) ? messages[0] : messages;

    if (typeof firstMessage === 'string') {
      accumulator[field as RestaurantFormField] = firstMessage;
    }

    return accumulator;
  }, {});

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
}

export function useAddRestaurant() {
  const [isLoading, setIsLoading] = useState(false);

  const addRestaurant = async (params: AddRestaurantParams): Promise<AddRestaurantResponse> => {
    setIsLoading(true);
    try {
      const response = await axiosServices.post('/restaurants', params);

      return { success: true, message: 'Restaurant added successfully', data: response.data?.data };
    } catch (error: any) {
      console.error('Error adding restaurant:', error);
      const fieldErrors = extractRestaurantFieldErrors(error);

      return {
        success: false,
        message: error?.message || 'Failed to add restaurant',
        fieldErrors
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { addRestaurant, isLoading };
}
